// eslint-disable-next-line import/no-extraneous-dependencies
import { ChangeEvent, SyntheticEvent } from "react"
import { SetURLSearchParams, useNavigate } from "react-router-dom"

import { LCTOnlyDatasets } from "../constants/enums"
import {
    DEMAND_DEFAULT_ORDER_BY,
    DEMAND_DEVICE_SYSTEM_REF_KW,
    IS_SUBSCRIBED,
    IS_THREAD_UNREAD,
    MAX_DEMAND_KW,
    MIN_DEMAND_KW,
    ORDER_BY_KW,
    ORDER_KW,
    TTR_DEFAULT_ORDER_DIRECTION,
} from "../constants/generalConstants"
import { DEMAND_DEVICE_SEARCH_NAV_LINK } from "../router/routes"
import {
    DEM_MANUFACTURER_URL_PARAM,
    DEM_MODEL_URL_PARAM,
    DEM_PHASE_URL_PARAM,
    DEM_STATUS_URL_PARAM,
    DEM_TYPE_URL_PARAM,
    DEM_VOLTAGE_URL_PARAM,
} from "../services/middleware/mappers/demandFilterReferenceMapper"
import { MAX_DEMAND } from "../webpages/lowCarbonTechDatabases/demandDeviceSearch"

/**
 * Hook for deriving and managing filter panel input values from URL search
 * params for DEMAND database search API. Search params are treated as the filter
 * panel state. This hook abstracts the translation of this state into objects
 * the filter panel inputs can read.
 *
 * @param searchParams - URL search parameters
 * @param setSearchParams - setter function for URL search parameters
 * @param isPopupMode - optional parameter to adjust behavior in popup mode
 * @returns - helper function for setting a filter value and values for all
 * database filter panel options.
 */
export const useDemandSearchURLFilterParams = (
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    isPopupMode: boolean
): {
    complianceStatus: string | null
    demandLb: number
    demandUb: number
    deviceType: string | number | null
    manufacturer: string | number | null
    modelRef: string
    phase: string | number | null
    orderBy: string
    orderDirection: string
    systemRef: string
    voltage: string | number | null
    isSubscribed: boolean
    isThreadUnread: boolean
    clearAllFilters: () => void
    setFilterFromTextfield: (newValue: string, searchParam: string) => void
    setFilterFromSlider: (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[]
    ) => void
    setFilterValue: (
        newValue: string | number | null,
        searchParam: string
    ) => void
    setOrderBy: (orderBy: string, orderDirection: string) => void
    toggleDataset: (e_: ChangeEvent<HTMLInputElement>, value: string) => void
} => {
    /**
     * Sets order by and order direction URL params
     * @param orderBy - column to be sorted on
     * @param orderDirection - sort direction (asc | desc)
     */
    const setOrderBy = (orderBy: string, orderDirection: string): void => {
        searchParams.set(ORDER_BY_KW, orderBy)
        searchParams.set(ORDER_KW, orderDirection)
        setSearchParams(searchParams)
    }

    /**
     * Updates URL search params for filter panel elements which are text
     * @param newValue - new value being set
     * @param searchParam - url search param for the textfield
     */
    const setFilterFromTextfield = (
        newValue: string,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    /**
     * Called when slider change is committed. Updates URL params.
     * @param e_ - MUI slider event, not used
     * @param newValue - array of upper and lower slider value
     */
    const setFilterFromSlider = (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[]
    ): void => {
        if (!Array.isArray(newValue)) {
            // TTR slider error: onSlideCommitted handle not returning array
            return
        }
        searchParams.delete(MIN_DEMAND_KW)
        searchParams.delete(MAX_DEMAND_KW)
        searchParams.set(
            MIN_DEMAND_KW,
            String((newValue as [number, number])[0])
        )
        if ((newValue as [number, number])[1] !== MAX_DEMAND) {
            searchParams.set(
                MAX_DEMAND_KW,
                String((newValue as [number, number])[1])
            )
        }
        setSearchParams(searchParams)
    }

    /**
     * Called when a filter value has been updated. Writes new value to URL
     * search params and triggers re-render via `setSearchParams` function.
     * Only to be used with auto completes where the options are a flat array.
     *
     * @param newValue - the new selected value
     */
    const setFilterValueFromAutocompleteKwarg = (
        newValue: string | number | null,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    const navigate = useNavigate()
    /** Updates URL when user selects a new demand dataset */
    const toggleDataset = (
        e_: ChangeEvent<HTMLInputElement>,
        value: string
    ): void => {
        navigate(DEMAND_DEVICE_SEARCH_NAV_LINK(value as LCTOnlyDatasets))
    }

    // Filter params
    const systemRef = searchParams.get(DEMAND_DEVICE_SYSTEM_REF_KW) || ""
    const manufacturer = searchParams.get(DEM_MANUFACTURER_URL_PARAM) || null
    const modelRef = searchParams.get(DEM_MODEL_URL_PARAM) || ""
    const phase = searchParams.get(DEM_PHASE_URL_PARAM) || null
    const voltage = searchParams.get(DEM_VOLTAGE_URL_PARAM) || null
    const deviceType = searchParams.get(DEM_TYPE_URL_PARAM) || null
    const complianceStatus = searchParams.get(DEM_STATUS_URL_PARAM) || null
    const demandLb = Number(searchParams.get(MIN_DEMAND_KW)) || 0
    const demandUb = Number(searchParams.get(MAX_DEMAND_KW)) || MAX_DEMAND
    const isSubscribed = searchParams.get(IS_SUBSCRIBED) === "true"
    const isThreadUnread = searchParams.get(IS_THREAD_UNREAD) === "true"

    // Sort params - these are defaulted
    const orderBy = searchParams.get(ORDER_BY_KW) || DEMAND_DEFAULT_ORDER_BY
    const orderDirection =
        searchParams.get(ORDER_KW) || TTR_DEFAULT_ORDER_DIRECTION

    const clearAllFilters = (): void => {
        setSearchParams(new URLSearchParams())
    }

    return {
        complianceStatus,
        demandLb,
        demandUb,
        deviceType,
        manufacturer,
        modelRef,
        phase,
        orderBy,
        orderDirection,
        systemRef,
        voltage,
        isSubscribed,
        isThreadUnread,
        clearAllFilters,
        toggleDataset,
        setFilterFromSlider,
        setFilterFromTextfield,
        setFilterValue: setFilterValueFromAutocompleteKwarg,
        setOrderBy,
    }
}
